import React from 'react';
import PropTypes from 'prop-types';
import { Tooltip } from '@material-ui/core';
import BreadcrumbLayout from '../../../shared/components/common/breadcrumbLayout';
import {
    Stepper,
    Modal,
    PrimaryButton,
    SecondaryButton,
    StatusPill,
    FeedbackState,
} from '../../../shared/ui';
import NotaRemessaSection from '../components/NotaRemessaSection';
import QuoteBudgetFileSection from '../../../shared/components/details/quoteBudgetFileSection';
import QuoteLaudoFileSection from '../../../shared/components/details/quoteLaudoFileSection';
import QuoteNfFileSection from '../../../shared/components/details/quoteNfFileSection';
import {
    ActionButtonsContainer,
    CardTitle,
    ContentArea,
    CyanCheckbox,
    HeaderArea,
    HeaderStatusRow,
    InfoGrid,
    InfoItem,
    NavigationButtonsContainer,
    PageContainer,
    PageShell,
    CheckboxLabel,
    SectionCard,
    ShippingActionButtonsContainer,
    StepHint,
    StepSection,
    StepperArea,
    TextList,
    WarningBox
} from '../styles';

const replaceToken = (value, replacements = []) => replacements.reduce(
    (accumulator, replacement, index) => accumulator.replace(`#vr${index}#`, replacement),
    String(value || '')
);

const formatCurrency = (value) =>
    Number(value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const formatDate = (dateValue) => {
    if (!dateValue) return '-';
    const parsed = new Date(dateValue);
    if (Number.isNaN(parsed.getTime())) return '-';
    return parsed.toLocaleDateString('pt-BR');
};

const mapEstimateItems = (itens, clientLang) => {
    let parsedItens = itens;

    if (typeof parsedItens === 'string') {
        try {
            parsedItens = JSON.parse(parsedItens);
        } catch (e) {
            // ignore error
        }
    }

    if (Array.isArray(parsedItens)) {
        if (parsedItens.length === 0) return ['-'];

        return parsedItens.map((item, index) => {
            const fallbackServiceName = replaceToken(
                clientLang.client_quote_details_item_fallback,
                [index + 1]
            );
            let serviceName = item?.servico || item?.nome || fallbackServiceName;
            let quantity = item?.quantidade || item?.qtd || 1;
            let details = '';

            if (item?.equipamento_info) {
                let eqInfo = item.equipamento_info;
                if (typeof eqInfo === 'string') {
                    try {
                        eqInfo = JSON.parse(eqInfo);
                    } catch (e) {
                        // Ignore error
                    }
                }
                if (typeof eqInfo === 'object' && eqInfo !== null) {
                    const marca = eqInfo.marca ? `Marca: ${eqInfo.marca}` : '';
                    const modelo = eqInfo.modelo ? `Modelo: ${eqInfo.modelo}` : '';
                    const problema = eqInfo.problema ? `Problema: ${eqInfo.problema}` : '';
                    
                    const infoParts = [marca, modelo, problema].filter(Boolean).join(' | ');
                    if (infoParts) {
                        details = ` - ${infoParts}`;
                    }
                    if (eqInfo.quantidade) {
                        quantity = eqInfo.quantidade;
                    }
                }
            }

            return `${serviceName} (${clientLang.client_quote_details_qty_short_label}: ${quantity})${details}`;
        });
    }

    if (typeof parsedItens === 'object' && parsedItens !== null) {
        let equipamentos = parsedItens.equipamentos || [];
        let servicos = parsedItens.servicos || [];
        let eqInfo = parsedItens.equipamento_info || {};
        
        let listToMap = equipamentos.length > 0 ? equipamentos : servicos;
        
        if (listToMap.length === 0) {
            if (Object.keys(eqInfo).length > 0) {
                listToMap = [{}];
            } else {
                return ['-'];
            }
        }

        return listToMap.map((item, index) => {
            const fallbackServiceName = replaceToken(
                clientLang.client_quote_details_item_fallback,
                [index + 1]
            );
            let serviceName = item?.nome || item?.servico || fallbackServiceName;

            return serviceName;
        });
    }

    return ['-'];
};

const ClientQuoteDetailsView = ({
    quoteData,
    isLoading,
    error,
    currentLanguage,
    steps,
    currentStep,
    statusVariant,
    shippingConfirmed,
    notaRemessaFile,
    canShowEstimateActions,
    canShowBudgetActions,
    onAcceptEstimate,
    onRefuseEstimate,
    onAcceptBudget,
    onRefuseBudget,
    onRefuseQuotation,
    onNextStep,
    onPrevStep,
    onToggleShippingConfirmed,
    onNotaRemessaFileChange,
    isFlowLocked,
    flowLockMessage,
    isNextDisabled,
    modalConfig,
    isPostAcceptance
}) => {
    const clientLang = currentLanguage.bepiService.client;
    const sharedLang = currentLanguage.bepiService.shared;

    const estimativa = quoteData?.estimativa;
    const cliente = quoteData?.cliente;
    const orcamento = quoteData?.orcamento;

    const quoteNumberLabel = replaceToken(
        clientLang.client_quote_details_quote_number_title,
        [estimativa?.id || '-']
    );

    const breadcrumbItems = [
        { label: sharedLang.shared_breadcrumb_home, path: '/bepi/client' },
        { label: sharedLang.shared_breadcrumb_quotations, path: '/bepi/client/quotations' },
        { label: quoteNumberLabel }
    ];

    if (isLoading) {
        return (
            <BreadcrumbLayout
                title={clientLang.client_quote_details_page_title}
                breadcrumbItems={breadcrumbItems}
                currentLanguage={currentLanguage}
                showBackButton
            >
                <FeedbackState
                    loading
                    loadingMessage={clientLang.client_quote_details_loading}
                />
            </BreadcrumbLayout>
        );
    }

    if (error) {
        return (
            <BreadcrumbLayout
                title={clientLang.client_quote_details_page_title}
                breadcrumbItems={breadcrumbItems}
                currentLanguage={currentLanguage}
                showBackButton
            >
                <FeedbackState error={error} />
            </BreadcrumbLayout>
        );
    }

    const normalizeStatus = (status) => (status || '').toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    const currentStatus = (estimativa?.status || orcamento?.status || '').toLowerCase();
    const canRefuseQuotation = currentStatus && normalizeStatus(currentStatus) === 'estimativa entregue';

    const renderStepContent = () => {
        if (currentStep === 0) {
            const estimateItems = mapEstimateItems(estimativa?.itens, clientLang);

            return (
                <StepSection>
                    <SectionCard>
                        <CardTitle>{clientLang.client_quote_details_client_data_title}</CardTitle>
                        <InfoGrid>
                            <InfoItem>
                                <label>{sharedLang.shared_resp_client_company}</label>
                                <span>{cliente?.empresa || '-'}</span>
                            </InfoItem>
                            <InfoItem>
                                <label>{clientLang.client_quote_details_client_contact_label}</label>
                                <span>{cliente?.nome || '-'}</span>
                            </InfoItem>
                            <InfoItem>
                                <label>{clientLang.client_quote_details_client_phone_label}</label>
                                <span>{cliente?.telefone || '-'}</span>
                            </InfoItem>
                            <InfoItem>
                                <label>{clientLang.client_quote_details_client_email_label}</label>
                                <span>{cliente?.email || '-'}</span>
                            </InfoItem>
                        </InfoGrid>
                    </SectionCard>

                    <SectionCard>
                        <CardTitle>{clientLang.client_quote_details_estimate_data_title}</CardTitle>
                        <InfoGrid>
                            <InfoItem>
                                <label>{clientLang.client_quote_details_num_label}</label>
                                <span>{estimativa?.id || '-'}</span>
                            </InfoItem>
                            <InfoItem>
                                <label>{sharedLang.shared_orcamento_status_label}</label>
                                <span>{estimativa?.status || '-'}</span>
                            </InfoItem>
                            <InfoItem>
                                <label>{clientLang.client_quote_details_location_label}</label>
                                <span>{estimativa?.endereco || '-'}</span>
                            </InfoItem>
                            <InfoItem>
                                <label>{clientLang.client_quote_details_response_label}</label>
                                <span>{estimativa?.texto_resposta || '-'}</span>
                            </InfoItem>
                            <InfoItem>
                                <label>{clientLang.client_quote_details_value_label}</label>
                                <span>{formatCurrency(estimativa?.valor_resposta)}</span>
                            </InfoItem>
                            <InfoItem>
                                <label>{clientLang.client_quote_details_items_label}</label>
                                {(() => {
                                    let parsedItens = estimativa?.itens;
                                    if (typeof parsedItens === 'string') {
                                        try { parsedItens = JSON.parse(parsedItens); } catch (e) { /* ignore */ }
                                    }
                                    const eqInfo = (parsedItens && !Array.isArray(parsedItens)) ? parsedItens.equipamento_info : null;

                                    return (
                                        <>
                                            <TextList>
                                                {estimateItems.map((itemDescription, index) => (
                                                    <li key={`${itemDescription}-${index}`}>{itemDescription}</li>
                                                ))}
                                            </TextList>
                                            {eqInfo && (
                                                <div style={{ marginTop: '0.5rem', padding: '0.5rem', background: 'rgba(0,0,0,0.03)', borderRadius: '6px' }}>
                                                    <div><strong>Marca:</strong> {eqInfo.marca || '-'}</div>
                                                    <div><strong>Modelo:</strong> {eqInfo.modelo || '-'}</div>
                                                    <div><strong>Problema Relatado:</strong> {eqInfo.problema || '-'}</div>
                                                    <div><strong>Quantidade:</strong> {eqInfo.quantidade || '-'}</div>
                                                </div>
                                            )}
                                        </>
                                    );
                                })()}
                            </InfoItem>
                        </InfoGrid>
                    </SectionCard>

                    <QuoteBudgetFileSection 
                        quoteId={estimativa?.id} 
                        role="client" 
                        currentLanguage={currentLanguage} 
                        serviceFiles={quoteData?.serviceFiles} 
                        status={estimativa?.status}
                    />
                    
                    <QuoteLaudoFileSection
                        quoteId={estimativa.id}
                        role="client"
                        currentLanguage={currentLanguage}
                        serviceFiles={quoteData?.serviceFiles}
                    />
                    
                    <QuoteNfFileSection
                        quoteId={estimativa.id}
                        role="client"
                        currentLanguage={currentLanguage}
                        serviceFiles={quoteData?.serviceFiles}
                    />

                    {isPostAcceptance && (
                        <SectionCard>
                            <CardTitle>{clientLang.client_quote_details_shipping_reference_title}</CardTitle>
                            <StepHint>{clientLang.client_quote_details_shipping_reference_hint}</StepHint>
                            <ShippingActionButtonsContainer $align='left'>
                                <PrimaryButton
                                    type='button'
                                    onClick={() => window.open(`/#/bepi/client/shipment/${estimativa?.id}`, '_blank', 'noopener,noreferrer')}
                                >
                                    {clientLang.client_quote_details_shipping_open_page_btn}
                                </PrimaryButton>
                            </ShippingActionButtonsContainer>
                        </SectionCard>
                    )}
                </StepSection>
            );
        }

        if (currentStep === 1) {
            return (
                <StepSection>
                    <SectionCard>
                        <CardTitle>{clientLang.client_quote_details_step_shipping}</CardTitle>
                        <StepHint>{clientLang.client_quote_details_shipping_title}</StepHint>

                        <TextList>
                            <li>{clientLang.client_shipment_procedure_step1}</li>
                            <li>{clientLang.client_shipment_procedure_step2}</li>
                            <li>{clientLang.client_shipment_procedure_step3}</li>
                        </TextList>

                        <WarningBox>
                            {clientLang.client_quote_details_validation_invoices}
                        </WarningBox>

                        <ShippingActionButtonsContainer $align='left'>
                            <PrimaryButton
                                type='button'
                                onClick={() => window.open(`/#/bepi/client/shipment/${estimativa?.id}`, '_blank', 'noopener,noreferrer')}
                            >
                                {clientLang.client_quote_details_shipping_open_page_btn}
                            </PrimaryButton>
                        </ShippingActionButtonsContainer>

                        <CheckboxLabel>
                            <CyanCheckbox
                                checked={shippingConfirmed}
                                onChange={(event) => onToggleShippingConfirmed(event.target.checked)}
                            />
                            {clientLang.client_quote_details_shipping_review_checkbox}
                        </CheckboxLabel>
                    </SectionCard>
                </StepSection>
            );
        }

        if (currentStep === 2) {
            return (
                <StepSection>
                    <NotaRemessaSection
                        notaRemessaFile={notaRemessaFile}
                        onNotaRemessaFileChange={onNotaRemessaFileChange}
                        currentLanguage={currentLanguage}
                    />
                </StepSection>
            );
        }

        return (
            <StepSection>
                <SectionCard>
                    <CardTitle>{clientLang.client_quote_details_step_checkout}</CardTitle>
                    <StepHint>{clientLang.client_quote_details_checkout_hint}</StepHint>
                    <InfoGrid>
                        <InfoItem>
                            <label>{clientLang.client_quote_details_num_label}</label>
                            <span>{estimativa?.id || '-'}</span>
                        </InfoItem>
                        <InfoItem>
                            <label>{sharedLang.shared_quotation_table_status}</label>
                            <span>{estimativa?.status || '-'}</span>
                        </InfoItem>
                        <InfoItem>
                            <label>{clientLang.client_quote_details_checkout_invoice_count}</label>
                            <span>{notaRemessaFile?.name || '-'}</span>
                        </InfoItem>
                    </InfoGrid>

                    <ActionButtonsContainer>
                        {canShowEstimateActions && (
                            <>
                                <PrimaryButton type='button' onClick={onAcceptEstimate}>
                                    {clientLang.client_quote_details_checkout_accept_estimate}
                                </PrimaryButton>
                                <SecondaryButton type='button' onClick={onRefuseEstimate}>
                                    {clientLang.client_quote_details_checkout_refuse_estimate}
                                </SecondaryButton>
                            </>
                        )}

                        {canShowBudgetActions && (
                            <>
                                <PrimaryButton type='button' onClick={onAcceptBudget}>
                                    {clientLang.client_quote_details_checkout_accept_budget}
                                </PrimaryButton>
                                <SecondaryButton type='button' onClick={onRefuseBudget}>
                                    {clientLang.client_quote_details_checkout_refuse_budget}
                                </SecondaryButton>
                            </>
                        )}
                    </ActionButtonsContainer>
                </SectionCard>
            </StepSection>
        );
    };

    return (
        <BreadcrumbLayout
            title={quoteNumberLabel}
            breadcrumbItems={breadcrumbItems}
            currentLanguage={currentLanguage}
            showBackButton
        >
            <PageContainer>
                <PageShell>
                    <HeaderArea style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <HeaderStatusRow>
                            <span className='title'>{clientLang.client_quote_details_current_status_title}</span>
                            <StatusPill variant={statusVariant} context='onDark'>
                                {estimativa?.status || orcamento?.status || clientLang.client_quote_details_no_status}
                            </StatusPill>
                        </HeaderStatusRow>
                        {canRefuseQuotation && (
                            <SecondaryButton variant='danger' size='sm' onClick={onRefuseQuotation}>
                                Recusar Cotação
                            </SecondaryButton>
                        )}
                    </HeaderArea>

                    <StepperArea>
                        <Stepper steps={steps} currentStep={currentStep} />
                    </StepperArea>

                    <ContentArea>
                        {renderStepContent()}
                        <NavigationButtonsContainer>
                            <SecondaryButton
                                type='button'
                                onClick={onPrevStep}
                                disabled={currentStep === 0}
                            >
                                {sharedLang.shared_breadcrumb_back_btn}
                            </SecondaryButton>
                            <Tooltip
                                title={isFlowLocked ? flowLockMessage : ''}
                                disableHoverListener={!isFlowLocked}
                                arrow
                            >
                                <span>
                                    <PrimaryButton
                                        type='button'
                                        onClick={onNextStep}
                                        disabled={isNextDisabled}
                                    >
                                        {sharedLang.shared_clients_pagination_next}
                                    </PrimaryButton>
                                </span>
                            </Tooltip>
                        </NavigationButtonsContainer>
                    </ContentArea>
                </PageShell>
            </PageContainer>

            <Modal {...modalConfig} />
        </BreadcrumbLayout>
    );
};

ClientQuoteDetailsView.propTypes = {
    quoteData: PropTypes.object,
    isLoading: PropTypes.bool,
    error: PropTypes.string,
    currentLanguage: PropTypes.object.isRequired,
    steps: PropTypes.arrayOf(PropTypes.string).isRequired,
    currentStep: PropTypes.number.isRequired,
    statusVariant: PropTypes.oneOf(['danger', 'info', 'success', 'warning']).isRequired,
    shippingConfirmed: PropTypes.bool.isRequired,
    notaRemessaFile: PropTypes.object,
    canShowEstimateActions: PropTypes.bool.isRequired,
    canShowBudgetActions: PropTypes.bool.isRequired,
    onAcceptEstimate: PropTypes.func.isRequired,
    onRefuseEstimate: PropTypes.func.isRequired,
    onAcceptBudget: PropTypes.func.isRequired,
    onRefuseBudget: PropTypes.func.isRequired,
    onRefuseQuotation: PropTypes.func.isRequired,
    onNextStep: PropTypes.func.isRequired,
    onPrevStep: PropTypes.func.isRequired,
    onToggleShippingConfirmed: PropTypes.func.isRequired,
    onNotaRemessaFileChange: PropTypes.func.isRequired,
    isFlowLocked: PropTypes.bool.isRequired,
    flowLockMessage: PropTypes.string.isRequired,
    isNextDisabled: PropTypes.bool.isRequired,
    modalConfig: PropTypes.shape({
        isOpen: PropTypes.bool.isRequired,
        type: PropTypes.string,
        title: PropTypes.string,
        message: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
        onConfirm: PropTypes.func,
        onCancel: PropTypes.func,
        confirmText: PropTypes.string,
        cancelText: PropTypes.string
    }).isRequired,
    isPostAcceptance: PropTypes.bool
};

ClientQuoteDetailsView.defaultProps = {
    quoteData: null,
    isLoading: false,
    error: null,
    notaRemessaFile: null,
    isPostAcceptance: false,
};

export default ClientQuoteDetailsView;
